/**
 * EditMessageModal Component
 *
 * Modal for editing messages within the 15-minute edit window.
 * Shows remaining edit time and validates content.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { differenceInSeconds } from 'date-fns';
import {
  Pencil,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import type { Message } from '@/types/messaging';

interface EditMessageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: Message | null;
  onSave: (messageId: string, content: string) => Promise<void>;
}

const MAX_MESSAGE_LENGTH = 5000;
const EDIT_WINDOW_MINUTES = 15;

/**
 * Calculate remaining edit time
 */
const getRemainingTime = (createdAt: string): { minutes: number; seconds: number; isExpired: boolean } => {
  const created = new Date(createdAt);
  const deadline = new Date(created.getTime() + EDIT_WINDOW_MINUTES * 60 * 1000);
  const now = new Date();

  if (now >= deadline) {
    return { minutes: 0, seconds: 0, isExpired: true };
  }

  const totalSeconds = differenceInSeconds(deadline, now);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return { minutes, seconds, isExpired: false };
};

export const EditMessageModal: React.FC<EditMessageModalProps> = ({
  open,
  onOpenChange,
  message,
  onSave,
}) => {
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [remainingTime, setRemainingTime] = useState<{ minutes: number; seconds: number; isExpired: boolean }>({
    minutes: EDIT_WINDOW_MINUTES,
    seconds: 0,
    isExpired: false,
  });

  // Initialize content when message changes
  useEffect(() => {
    if (message && open) {
      setContent(message.content);
      setRemainingTime(getRemainingTime(message.createdAt));
    }
  }, [message, open]);

  // Update remaining time every second
  useEffect(() => {
    if (!open || !message) return;

    const interval = setInterval(() => {
      const time = getRemainingTime(message.createdAt);
      setRemainingTime(time);

      // Close modal if time expired
      if (time.isExpired) {
        toast.error('Edit window has expired');
        onOpenChange(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [open, message, onOpenChange]);

  // Validation
  const validation = useMemo(() => {
    if (!content.trim()) {
      return { isValid: false, error: 'Message cannot be empty' };
    }
    if (content.length > MAX_MESSAGE_LENGTH) {
      return { isValid: false, error: `Message exceeds ${MAX_MESSAGE_LENGTH} characters` };
    }
    if (message && content.trim() === message.content.trim()) {
      return { isValid: false, error: 'No changes made' };
    }
    if (remainingTime.isExpired) {
      return { isValid: false, error: 'Edit window has expired' };
    }
    return { isValid: true, error: null };
  }, [content, message, remainingTime.isExpired]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!message || !validation.isValid) return;

    setIsSaving(true);

    try {
      await onSave(message.id, content.trim());
      onOpenChange(false);
    } catch (error) {
      console.error('[EditMessageModal] Save error:', error);
      // Error toast is handled by the store
    } finally {
      setIsSaving(false);
    }
  }, [message, content, validation.isValid, onSave, onOpenChange]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (validation.isValid && !isSaving) {
          handleSave();
        }
      }
    },
    [validation.isValid, isSaving, handleSave]
  );

  if (!message) return null;

  const characterCount = content.length;
  const isNearLimit = characterCount > MAX_MESSAGE_LENGTH * 0.9;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
            Edit Message
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between">
            <span>Edit your message</span>
            <span
              className={`flex items-center gap-1 text-xs ${
                remainingTime.minutes < 2
                  ? 'text-destructive'
                  : remainingTime.minutes < 5
                  ? 'text-amber-500'
                  : 'text-muted-foreground'
              }`}
            >
              <Clock className="h-3 w-3" />
              {remainingTime.minutes}:{remainingTime.seconds.toString().padStart(2, '0')} remaining
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning if time is running low */}
          {remainingTime.minutes < 2 && !remainingTime.isExpired && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md text-amber-700 dark:text-amber-400 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>Edit window closing soon. Save your changes.</span>
            </div>
          )}

          {/* Edit textarea */}
          <div className="relative">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              className="min-h-[100px] resize-none pr-16"
              autoFocus
            />
            <span
              className={`absolute bottom-2 right-2 text-xs ${
                isNearLimit
                  ? characterCount > MAX_MESSAGE_LENGTH
                    ? 'text-destructive'
                    : 'text-amber-500'
                  : 'text-muted-foreground'
              }`}
            >
              {characterCount}/{MAX_MESSAGE_LENGTH}
            </span>
          </div>

          {/* Validation error */}
          {validation.error && content.trim() !== message.content.trim() && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {validation.error}
            </p>
          )}

          {/* Hint */}
          <p className="text-xs text-muted-foreground">
            Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">⌘</kbd> + <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Enter</kbd> to save
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !validation.isValid}
            style={{
              backgroundColor: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
            }}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Pencil className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditMessageModal;
